import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Hourglass, MessageCircle, User, UserCheck, X } from "lucide-react";
import { siteTitleInline } from "@/components/home/SiteTitle";

export const SiteModals = ({ modal, onClose, content, regMessage }) => (
  <AnimatePresence>
    {modal && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] grid place-items-center p-6"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-[#0F1B16]/40 backdrop-blur-sm" />
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`modal-${modal}`}
          className="relative bg-background border border-border rounded-3xl shadow-2xl w-full max-w-md p-8"
        >
          <button
            data-testid="modal-close" onClick={onClose}
            className={`absolute top-5 right-5 grid place-items-center w-9 h-9 rounded-full border transition-colors z-10 ${["registered", "pending", "duplicate"].includes(modal) ? "bg-white/15 border-white/25 text-white hover:bg-white/25" : "border-border hover:bg-muted"}`}
          >
            <X size={16} />
          </button>

          {modal === "about" && (
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-accent mb-5">About</p>
              <h3 className="font-serif text-3xl leading-tight mb-6">Program Organizer</h3>
              <div className="flex items-center gap-4">
                <span className="grid place-items-center w-14 h-14 rounded-full bg-accent/10 text-accent">
                  <User size={24} />
                </span>
                <div>
                  <p className="font-serif text-2xl">{content?.organizer_name || "Naveen Vangari"}</p>
                  <p className="text-sm text-muted-foreground">{siteTitleInline(content)}</p>
                </div>
              </div>
            </div>
          )}

          {modal === "registered" && (
            <div className="-m-8 overflow-hidden rounded-3xl">
              <div className="px-8 pt-8 pb-7 text-white text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg,#0F1B16 0%,#1A2E26 55%,#2F6B4F 100%)" }}>
                <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
                <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-emerald-400 text-[#0F1B16] mb-4 shadow-[0_10px_30px_rgba(16,185,129,0.45)]"><CheckCircle2 size={30} /></span>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F3C9A8] mb-1.5">Step 3 · Done</p>
                <h3 className="font-serif text-3xl leading-tight">Registration completed</h3>
              </div>
              <div className="px-8 py-7 text-center bg-[#FBFAF6]">
                <p data-testid="registration-success-message" className="text-sm text-[#1A2E26]/80 leading-relaxed mb-6 whitespace-pre-line rounded-2xl border border-dashed border-[#2F6B4F]/30 bg-[#EAF6EE] p-4">
                  {regMessage || "Thank you for registering. Our team will reach out with the next steps."}
                </p>
                <button data-testid="registration-success-done" onClick={onClose} className="inline-flex items-center justify-center rounded-full px-10 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#C86A51] to-[#E08A5B] shadow-[0_10px_30px_rgba(200,106,81,0.35)] hover:-translate-y-0.5 transition-transform duration-300">Done</button>
              </div>
            </div>
          )}

          {modal === "pending" && (
            <div className="-m-8 overflow-hidden rounded-3xl" data-testid="approval-pending-modal">
              <div className="px-8 pt-8 pb-7 text-white text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg,#8A5A12 0%,#B57E17 55%,#D9A441 100%)" }}>
                <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/15 blur-2xl" />
                <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-white text-[#B57E17] mb-4 shadow-lg"><Hourglass size={30} /></span>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-1.5">Step 3 · Almost there</p>
                <h3 className="font-serif text-3xl leading-tight">Approval Pending!!!</h3>
              </div>
              <div className="px-8 py-7 text-center bg-[#FBFAF6]">
                <div data-testid="approval-pending-message" className="text-sm text-[#1A2E26]/80 leading-relaxed mb-6 space-y-1.5 rounded-2xl border border-dashed border-[#B57E17]/40 bg-[#FFF4E6] p-4">
                  <p>Registration has been completed successfully.</p>
                  <p>Final approval is required to join the batch.</p>
                  <p>You will be notified once approved.</p>
                </div>
                <button data-testid="approval-pending-done" onClick={onClose} className="inline-flex items-center justify-center rounded-full px-10 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#C86A51] to-[#E08A5B] shadow-[0_10px_30px_rgba(200,106,81,0.35)] hover:-translate-y-0.5 transition-transform duration-300">Done</button>
              </div>
            </div>
          )}

          {modal === "duplicate" && (
            <div className="-m-8 overflow-hidden rounded-3xl">
              <div className="px-8 pt-8 pb-7 text-white text-center relative overflow-hidden" style={{ background: "linear-gradient(135deg,#7A2E3A 0%,#A64B57 60%,#C86A51 100%)" }}>
                <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
                <span className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-white text-[#C86A51] mb-4 shadow-lg"><UserCheck size={30} /></span>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#F3C9A8] mb-1.5">Good news</p>
                <h3 className="font-serif text-3xl leading-tight">Already registered.</h3>
              </div>
              <div className="px-8 py-7 text-center bg-[#FBFAF6]">
                <p data-testid="registration-duplicate-message" className="text-sm text-[#1A2E26]/80 leading-relaxed mb-6 whitespace-pre-line rounded-2xl border border-dashed border-[#C86A51]/30 bg-[#FFF4EC] p-4">
                  {regMessage || "You have already registered"}. We have your details — see you in the program! For any changes, message us on WhatsApp.
                </p>
                <button data-testid="registration-duplicate-done" onClick={onClose} className="inline-flex items-center justify-center rounded-full px-10 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#C86A51] to-[#E08A5B] shadow-[0_10px_30px_rgba(200,106,81,0.35)] hover:-translate-y-0.5 transition-transform duration-300">Done</button>
              </div>
            </div>
          )}

          {modal === "contact" && (
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-accent mb-5">Contact</p>
              <h3 className="font-serif text-3xl leading-tight mb-2">Reach out on WhatsApp</h3>
              <p className="text-sm text-muted-foreground mb-7">Tap a number to open a WhatsApp chat.</p>
              <div className="space-y-3">
                {(content?.contact_numbers || "9000571911 / 9000571912").split(/[/,]/).map((raw) => raw.replace(/\D/g, "")).filter(Boolean).map((num, i) => (
                  <a
                    key={num + i}
                    href={`https://wa.me/${num.startsWith("91") ? num : "91" + num}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid={`contact-number-${i + 1}`}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-border px-5 py-4 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300 group"
                  >
                    <span className="flex items-center gap-3">
                      <MessageCircle size={18} className="text-accent group-hover:text-primary-foreground transition-colors" />
                      {num}
                    </span>
                    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  </a>
                ))}
              </div>
              <p className="mt-5 text-xs text-muted-foreground">WhatsApp only · Language: Telugu</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
